import { TableColumnsType } from 'antd';
import classNames from 'classnames';
import { KitIcon } from 'components/Icons/KitIcon';
import { TaskIcon } from 'components/Icons/TaskIcon';
import moment from 'moment';
import { CancelKitIcon } from 'pages/Radar/RadarKits/components/icons/CancelKitIcon';
import { ResendKitIcon } from 'pages/Radar/RadarKits/components/icons/ResendKitIcon';
import { Icons } from 'pages/Workshop/Icons';
import { Button, Tooltip } from 'components-antd';

import styles from './styles.module.scss';
import tableStyles from '../../components/Table/styles.module.scss';
import { DashIcon } from '../components';
import parentStyles from '../styles.module.scss';
import { useParams, useHistory } from 'react-router-dom';
import { link } from 'settings/navigation/link';

export type ClientInfoType = {
  FirstName: string;
  LastName: string;
  Id: string;
};

export type PastDataType = {
  key: string;
  name: {
    title: string;
    info: string;
  };
  completed: {
    on: string;
    by: string;
  };
  isOpened: {
    date: string;
    time: string;
  } | null;
  isClicked: {
    date: string;
    time: string;
  } | null;
  isReplied: {
    date: string;
    time: string;
  } | null;
  replies: {
    HTMLBody: string;
    CreatedDate: string | Date;
    From: string;
    Headers: Record<string, string>;
    Subject: string;
    To: string;
    TextBody: string;
    CC: string;
    Id: number;
  }[];
};

export type UpcomingDataType = {
  key: string;
  name: {
    title: string;
    info: string;
  };
  assignee: string;
  scheduled: string;
};

export type FailedDataType = {
  key: string;
  name: {
    title: string;
    info: string;
  };
  failed: string;
  error: string;
  id: number;
  clientId: number;
};

export type OverdueTaskType = {
  name: string;
  assignee: string;
  scheduled: string;
};

export type CancelledDataType = {
  name: string;
  assignee: string;
  canceled: Date | string;
};

export type UseCampaignClientExecInfoProps = {
  onCancel?: (execId, clientId) => void;
  onRetry?: (execId, clientId) => void;
  onReplyClick?: (record) => void;
  isConsolidatedView?: boolean;
};

export const useCampaignClientExecInfo = (props?: UseCampaignClientExecInfoProps) => {
  const { onCancel, onRetry, onReplyClick, isConsolidatedView = false } = props || {};
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const navigateToAssignedClientView = (clientId: string) => {
    history.push(link.toRadarKitSequenceAssignedClientView(id, clientId));
  };

  const ClientInfoColumn = {
    title: 'Client',
    dataIndex: 'clientInfo',
    key: 'clientInfo',
    className: tableStyles.tableCol,
    sorter: (a, b) => {
      const nameA = `${a?.clientInfo?.FirstName || ''} ${a?.clientInfo?.LastName || ''}`.trim();
      const nameB = `${b?.clientInfo?.FirstName || ''} ${b?.clientInfo?.LastName || ''}`.trim();
      return nameA.localeCompare(nameB);
    },
    render: (clientInfo) => {
      let clientName = `${clientInfo?.FirstName || ''} ${clientInfo?.LastName || ''}`.trim();
      if (!clientName) {
        clientName = '-';
      }
      return (
        <div
          className={styles.clientInfo}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (clientInfo?.Id) {
              navigateToAssignedClientView(clientInfo?.Id);
            }
          }}
        >
          {clientName}
        </div>
      );
    },
  };

  const pastColumns: TableColumnsType<PastDataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.name.title.localeCompare(b.name.title),
      render: (group: any) => (
        <div className={parentStyles.tableColNameWrapper}>
          <div className={parentStyles.iconWrapper}>
            {group.info === 'KIT' ? <KitIcon /> : <TaskIcon />}
          </div>
          <div className={parentStyles.detailsWrapper}>
            <div className={parentStyles.title}>{group.title}</div>
            <div className={parentStyles.info}>{group.info}</div>
          </div>
        </div>
      ),
    },
    ...(isConsolidatedView ? [ClientInfoColumn] : []),
    {
      title: 'Completed',
      dataIndex: 'completed',
      key: 'completed',
      className: tableStyles.tableCol,
      sorter: (a, b) => moment(a.completed.on).diff(moment(b.completed.on)),
      render: (completed: any) => (
        <div className={parentStyles.tableColNameWrapper}>
          <div className={parentStyles.detailsWrapper}>
            <div className={classNames(parentStyles.title, parentStyles.fw400)}>{completed.on}</div>
            <div className={parentStyles.info}>{completed.by}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Opened',
      dataIndex: 'isOpened',
      key: 'isOpened',
      className: tableStyles.tableCol,
      sorter: (a, b) => (a.isOpened ? 1 : -1),
      render: (isOpened: { date: string; time: string } | null) => (
        <div className={tableStyles.tableNormalCell}>
          {isOpened ? (
            <div className={styles.checkmark}>
              <Icons variant={Icons.GREEN_LIGHT_CHECKMARK} />
            </div>
          ) : (
            <div className={styles.icon}>
              <DashIcon />
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Clicked',
      dataIndex: 'isClicked',
      key: 'isClicked',
      className: tableStyles.tableCol,
      sorter: (a, b) => (a.isClicked ? 1 : -1),
      render: (isClicked: { date: string; time: string } | null) => (
        <div className={tableStyles.tableNormalCell}>
          {isClicked ? (
            <div className={styles.checkmark}>
              <Icons variant={Icons.GREEN_LIGHT_CHECKMARK} />
            </div>
          ) : (
            <div className={styles.icon}>
              <DashIcon />
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Replied',
      dataIndex: 'isReplied',
      key: 'isReplied',
      className: tableStyles.tableCol,
      sorter: (a, b) => (a.isReplied ? 1 : -1),
      render: (RepliedDate: { date: string; time: string } | null, record) => (
        <div className={tableStyles.tableNormalCell}>
          {RepliedDate ? (
            <div
              className={classNames(styles.checkmark, styles.cursor)}
              onClick={() => onReplyClick?.(record)}
            >
              <Icons variant={Icons.GREEN_LIGHT_CHECKMARK} />
            </div>
          ) : (
            <div className={styles.icon}>
              <DashIcon />
            </div>
          )}
        </div>
      ),
    },
  ];

  const upcomingColumns: TableColumnsType<UpcomingDataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.name.title.localeCompare(b.name.title),
      render: (group: any) => (
        <div className={parentStyles.tableColNameWrapper}>
          <div className={parentStyles.iconWrapper}>
            {group.info === 'KIT' ? <KitIcon /> : <TaskIcon />}
          </div>
          <div className={parentStyles.detailsWrapper}>
            <div className={parentStyles.title}>{group.title}</div>
            <div className={parentStyles.info}>{group.info}</div>
          </div>
        </div>
      ),
    },
    ...(isConsolidatedView
      ? [ClientInfoColumn]
      : [
          {
            title: 'Assignee',
            dataIndex: 'assignee',
            key: 'assignee',
            className: tableStyles.tableCol,
            render: (assignee) => assignee || 'Automatic',
          },
        ]),
    {
      title: 'Scheduled',
      defaultSortOrder: 'ascend',
      dataIndex: 'scheduled',
      key: 'scheduled',
      className: tableStyles.tableCol,
      sorter: (a, b) => {
        const dateA = moment(a.scheduled, 'MM/DD/YYYY');
        const dateB = moment(b.scheduled, 'MM/DD/YYYY');
        return dateA.isValid() && dateB.isValid() ? dateA.diff(dateB) : 0;
      },
    },
  ];

  const failedColumns: TableColumnsType<FailedDataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.name.title.localeCompare(b.name.title),
      render: (group: any, { key }) => (
        <div className={parentStyles.tableColNameWrapper} key={key}>
          <div className={parentStyles.iconWrapper}>
            {group.info === 'KIT' ? <KitIcon /> : <TaskIcon />}
          </div>
          <div className={parentStyles.detailsWrapper}>
            <div className={parentStyles.title}>{group.title}</div>
            <div className={parentStyles.info}>{group.info}</div>
          </div>
        </div>
      ),
    },
    ...(isConsolidatedView ? [ClientInfoColumn] : []),
    {
      title: 'Failed',
      dataIndex: 'failed',
      key: 'failed',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.failed.localeCompare(b.failed),
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.error.localeCompare(b.error),
      render: (error: string) => (
        <div className={tableStyles.tableNormalCell}>
          <div className={styles.errorTableText}>{error}</div>
        </div>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'isRetryAble',
      key: 'isRetryAble',
      className: tableStyles.tableCol,
      render: (isRetryAble: boolean, record) => (
        <div className={tableStyles.tableNormalCell}>
          <div className={styles.retryCancelWrapper}>
            <Tooltip
              placement="top"
              getPopupContainer={(trigger) => trigger}
              overlayClassName={classNames(styles.compareOfferTooltip, 'mosaikTooltip')}
              title={
                <p className={styles.descInfo}>
                  {!isRetryAble
                    ? 'This KIT cannot be resent until the error is addressed.'
                    : 'Send Again'}
                </p>
              }
            >
              <Button
                variant="default"
                disabled={!isRetryAble}
                className={classNames(styles.iconButton, styles.cursor, {
                  [parentStyles.op50]: !isRetryAble,
                })}
                onClick={() => onRetry?.(record.id, record?.clientId)}
                icon={<ResendKitIcon />}
              />
            </Tooltip>

            <Tooltip
              placement="top"
              getPopupContainer={(trigger) => trigger}
              overlayClassName={classNames(styles.compareOfferTooltip, 'mosaikTooltip')}
              title={<p className={styles.descInfo}>Cancel</p>}
            >
              <Button
                variant="default"
                className={classNames(styles.iconButton, styles.cursor)}
                onClick={() => onCancel?.(record.id, record?.clientId)}
                icon={<CancelKitIcon />}
              />
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  const overdueColumns: TableColumnsType<OverdueTaskType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: any) => (
        <div className={parentStyles.tableColNameWrapper}>
          <div className={parentStyles.iconWrapper}>
            <TaskIcon />
          </div>
          <div className={parentStyles.detailsWrapper}>
            <div className={parentStyles.title}>{name}</div>
            <div className={parentStyles.info}>{'TASK'}</div>
          </div>
        </div>
      ),
    },
    ...(isConsolidatedView
      ? [ClientInfoColumn]
      : [
          {
            title: 'Assignee',
            dataIndex: 'assignee',
            key: 'assignee',
            className: tableStyles.tableCol,
            render: (assignee) => assignee || 'Automatic',
          },
        ]),
    {
      title: 'Scheduled',
      defaultSortOrder: 'ascend',
      dataIndex: 'scheduled',
      key: 'scheduled',
      className: tableStyles.tableCol,
      sorter: (a, b) => {
        const dateA = moment(a.scheduled, 'MM/DD/YYYY');
        const dateB = moment(b.scheduled, 'MM/DD/YYYY');
        return dateA.isValid() && dateB.isValid() ? dateA.diff(dateB) : 0;
      },
      render: (scheduled: string) => (
        <div className={tableStyles.tableNormalCell}>
          <div className={styles.errorTableText}>{scheduled}</div>
        </div>
      ),
    },
  ];

  const canceledColumns: TableColumnsType<CancelledDataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => (
        <div className={parentStyles.tableColNameWrapper}>
          <div className={parentStyles.iconWrapper}>
            <KitIcon />
          </div>
          <div className={parentStyles.detailsWrapper}>
            <div className={parentStyles.title}>{name}</div>
            <div className={parentStyles.info}>{'KIT'}</div>
          </div>
        </div>
      ),
    },
    ...(isConsolidatedView
      ? [ClientInfoColumn]
      : [
          {
            title: 'Assignee',
            dataIndex: 'assignee',
            key: 'assignee',
            className: tableStyles.tableCol,
            render: (assignee) => assignee || 'Automatic',
          },
        ]),
    {
      title: 'Canceled',
      dataIndex: 'canceled',
      key: 'canceled',
      className: tableStyles.tableCol,
      sorter: (a, b) => moment(a.canceled).diff(moment(b.canceled)),
    },
  ];

  return {
    pastColumns,
    upcomingColumns,
    failedColumns,
    overdueColumns,
    canceledColumns,
  };
};
